import { Stack, Typography } from '@mui/material';
import { getCategories } from 'API/compassRights';
import {
  EntitlementsContext,
  ICategoriesState,
} from 'Context/CompassRightsContext/EntitlementsProvider';
import { useAppSelector, useDictionary } from 'Hooks';
import { useIsDesktop } from 'Hooks/useIsDesktop';
import { useContext, useEffect, useState } from 'react';
import { SelectCategory } from './SelectCategory';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import {
  EmploymentIcon,
  FromHeart,
  RightsTypes,
  SocialHelp,
  Statoscope,
} from 'Images';
import { ENTITLEMENT_TYPES } from 'Enum/CompassRights';

export interface SelectedCategory {
  id: number;
  subCategoryIds: number[];
}

const initialSelectedCategories = [
  { id: 1, subCategoryIds: new Array<number>() },
  { id: 2, subCategoryIds: new Array<number>() },
  { id: 3, subCategoryIds: new Array<number>() },
  { id: 4, subCategoryIds: new Array<number>() },
];

const entitlementTypeId = 5;

export const FilterTags = () => {
  const {
    categories,
    dispatchCategories,
    dispatchEntitlements,
    entitlementTypes,
    setEntitlementTypes,
  } = useContext(EntitlementsContext);
  const [selectedCategories, setSelectedCategories] = useState<
    SelectedCategory[]
  >(initialSelectedCategories);
  const dictionary = useDictionary(Dictionary, DICTIONARY.CompassRights);
  const isDesktop = useIsDesktop();
  const imposterId = useAppSelector(
    ({ imposter: { imposterId } }) => imposterId
  );

  useEffect(() => {
    const fetchCategories = async () => {
      const result = await getCategories();
      if (result)
        dispatchCategories({
          type: 'INITIALIZATION',
          payload: { value: result },
        });
    };
    fetchCategories();
  }, [imposterId, dispatchCategories]);

  const mapSelectedSubCategoriesIntoIds = (
    categoryId: number,
    selected: string[]
  ) => {
    const selectedCategories = categories.find(
      (category) => category.id === categoryId
    );

    //this should never happen
    if (!selectedCategories) throw new Error('Category ID not found');

    const subCategoryIds = selected.flatMap((subCategoryName) =>
      selectedCategories.subCategories
        .filter((subCategory) => subCategory.name === subCategoryName)
        .map((subCategory) => subCategory.id)
    );

    return subCategoryIds;
  };

  const toggleAndGetUniqueSubCategories = (
    categoryId: number,
    subCategoryIds: number[]
  ) => {
    const selectedSubCategories = selectedCategories.find(
      (selectedCategory) => selectedCategory.id === categoryId
    );

    if (!selectedSubCategories) {
      return {
        id: categoryId,
        subCategoryIds: subCategoryIds,
      };
    }
    const unique = new Array<number>();

    subCategoryIds.forEach((id) => {
      unique.push(id);
    });

    return {
      id: categoryId,
      subCategoryIds: unique,
    };
  };

  useEffect(() => {
    const newSelectedCategories = categories.map((category) => {
      if (category.checked)
        return {
          id: category.id,
          subCategoryIds: category.subCategories.map(
            (subCategory) => subCategory.id
          ),
        };

      return {
        id: category.id,
        subCategoryIds: category.subCategories
          .filter((subCategory) => subCategory.checked)
          .map((subCategory) => subCategory.id),
      };
    });

    setSelectedCategories(newSelectedCategories);
  }, [categories]);

  const handleOnChange = (categoryId: number, selected: string[]) => {
    const selectedSubCategoryIds = mapSelectedSubCategoriesIntoIds(
      categoryId,
      selected
    );

    const uniqueSubCategories = toggleAndGetUniqueSubCategories(
      categoryId,
      selectedSubCategoryIds
    );

    dispatchCategories({
      type: 'FILTER_CHECKBOX',
      payload: {
        value: {
          mainCat: uniqueSubCategories.id,
          subCat: uniqueSubCategories.subCategoryIds,
        },
      },
    });

    dispatchEntitlements({
      type: 'FILTER_CATEGORIES',
      payload: { value: categories },
    });
  };

  const getSelectedSubCategories = (categoryId: number) => {
    const selected = selectedCategories.find(
      (selected) => selected.id === categoryId
    );

    if (!selected)
      return { id: categoryId, subCategoryIds: new Array<number>() };
    return selected;
  };

  const filterLabels: { [index: number]: { img: string; label: string } } = {
    1: { img: FromHeart, label: dictionary.filters.welfare },
    2: { img: Statoscope, label: dictionary.filters.medical },
    3: { img: SocialHelp, label: dictionary.filters.social },
    4: { img: EmploymentIcon, label: dictionary.filters.employment },
  };

  return (
    <Stack mt={'24px'} gap={'24px'}>
      <Typography variant={'body1'} color={'text.secondary'}>
        {dictionary.filterByCategory}
      </Typography>
      <Stack direction={isDesktop ? 'row' : 'column'} gap={'10px'}>
        {selectedCategories &&
          categories.map((category: ICategoriesState) => (
            <SelectCategory
              key={category.id}
              categoryId={category.id}
              subCategories={category.subCategories}
              onChange={handleOnChange}
              selected={getSelectedSubCategories(category.id)}
              label={filterLabels[category.id].label}
              icon={filterLabels[category.id].img}
              width={
                isDesktop
                  ? category.id === 1
                    ? '300px'
                    : category.id === 4
                    ? '180px'
                    : '250px'
                  : '100%'
              }
            />
          ))}
        <SelectCategory
          key={entitlementTypeId}
          categoryId={entitlementTypeId}
          subCategories={[
            {
              id: ENTITLEMENT_TYPES.automatic,
              name: dictionary.entitlements.automatic,
            },
            {
              id: ENTITLEMENT_TYPES.singleUse,
              name: dictionary.entitlements.singleUse,
            },
          ]}
          onChangeBypass={(e) => {
            setEntitlementTypes(e.target.value as string[]);
          }}
          selected={{ id: 5, subCategoryIds: entitlementTypes }}
          label={dictionary.filters.rightsTypes}
          icon={RightsTypes}
          width={isDesktop ? '200px' : '100%'}
        />
      </Stack>
    </Stack>
  );
};
